import React, { useState } from 'react';
import { useLanguage } from '../context/LanguageContext';
import Header from '../components/layout/Header';
import Footer from '../components/layout/Footer';
import { Handshake, Building2, Globe, Heart, Mail, CheckCircle, Send, Loader2 } from 'lucide-react';
import { Button } from '../components/ui/button';
import { Input } from '../components/ui/input';
import { Label } from '../components/ui/label';
import apiClient from '../services/api';

const PartnersPage = () => {
  const { t } = useLanguage();
  const [formData, setFormData] = useState({
    company_name: '',
    contact_name: '',
    email: '',
    partnership_type: 'corporate',
    message: ''
  });
  const [loading, setLoading] = useState(false);
  const [submitted, setSubmitted] = useState(false);
  const [error, setError] = useState('');

  const handleSubmit = async (e) => {
    e.preventDefault();
    setLoading(true);
    setError('');

    try {
      await apiClient.post('/enquiries/partnership', formData);
      setSubmitted(true);
    } catch (err) {
      console.error('Partnership submission error:', err);
      setError('Failed to submit. Please try again later.');
    } finally {
      setLoading(false);
    }
  };

  if (submitted) {
    return (
      <div className="min-h-screen bg-background">
        <Header />
        <main className="py-20">
          <div className="max-w-lg mx-auto px-4 text-center">
            <div className="w-20 h-20 bg-gradient-to-br from-emerald-500 to-emerald-500 rounded-full flex items-center justify-center mx-auto mb-6">
              <CheckCircle className="w-10 h-10 text-white" />
            </div>
            <h1 className="text-3xl font-bold text-foreground mb-4">Thank You!</h1>
            <p className="text-lg text-muted-foreground mb-6">
              We've received your partnership inquiry. Our team will review your submission and contact you within 48 hours.
            </p>
            <p className="text-sm text-muted-foreground mb-8">
              In the meantime, feel free to explore our platform and see how we're helping people make a difference.
            </p>
            <Button 
              onClick={() => window.location.href = '/'}
              className="bg-gradient-to-r from-emerald-500 to-teal-600"
            >
              Explore FundTheHope
            </Button>
          </div>
        </main>
        <Footer />
      </div>
    );
  }

  return (
    <div className="min-h-screen bg-background">
      <Header />
      <main className="py-12">
        <div className="max-w-5xl mx-auto px-4 sm:px-6 lg:px-8">
          {/* Header */}
          <div className="text-center mb-12">
            <div className="w-16 h-16 bg-gradient-to-br from-emerald-500 to-teal-600 rounded-2xl flex items-center justify-center mx-auto mb-6">
              <Handshake className="w-8 h-8 text-white" />
            </div>
            <h1 className="text-4xl font-bold text-foreground mb-4">Partner With Us</h1>
            <p className="text-xl text-muted-foreground max-w-2xl mx-auto">
              Join us in making a difference. Together, we can amplify impact and reach more people in need.
            </p>
          </div>

          <div className="grid lg:grid-cols-2 gap-12">
            {/* Left Side - Info */}
            <div>
              {/* Partnership Types */}
              <div className="space-y-6 mb-10">
                <div className="bg-card border border-border rounded-xl p-6 hover:border-emerald-500/50 transition-colors">
                  <Building2 className="w-10 h-10 text-emerald-500 mb-4" />
                  <h2 className="text-xl font-semibold text-foreground mb-2">Corporate Partners</h2>
                  <p className="text-muted-foreground text-sm mb-4">
                    Partner with us for employee giving programs, CSR initiatives, and cause marketing campaigns.
                  </p>
                  <ul className="text-sm text-muted-foreground space-y-2">
                    <li className="flex items-center gap-2">
                      <CheckCircle className="w-4 h-4 text-emerald-500" />
                      Matching gift programs
                    </li>
                    <li className="flex items-center gap-2">
                      <CheckCircle className="w-4 h-4 text-emerald-500" />
                      Employee fundraising tools
                    </li>
                    <li className="flex items-center gap-2">
                      <CheckCircle className="w-4 h-4 text-emerald-500" />
                      Brand partnership opportunities
                    </li>
                  </ul>
                </div>

                <div className="bg-card border border-border rounded-xl p-6 hover:border-teal-500/50 transition-colors">
                  <Globe className="w-10 h-10 text-teal-500 mb-4" />
                  <h2 className="text-xl font-semibold text-foreground mb-2">Nonprofit Partners</h2>
                  <p className="text-muted-foreground text-sm mb-4">
                    Leverage our platform to expand your fundraising reach and connect with new donors.
                  </p>
                  <ul className="text-sm text-muted-foreground space-y-2">
                    <li className="flex items-center gap-2">
                      <CheckCircle className="w-4 h-4 text-teal-500" />
                      Dedicated support team
                    </li>
                    <li className="flex items-center gap-2">
                      <CheckCircle className="w-4 h-4 text-teal-500" />
                      Custom branded pages
                    </li>
                    <li className="flex items-center gap-2">
                      <CheckCircle className="w-4 h-4 text-teal-500" />
                      Analytics & reporting
                    </li>
                  </ul>
                </div>
              </div>

              {/* Why Partner */}
              <div className="bg-gradient-to-br from-emerald-500/10 to-teal-500/10 rounded-2xl p-6 border border-emerald-500/20">
                <h3 className="text-lg font-semibold text-foreground mb-4 flex items-center gap-2">
                  <Heart className="w-5 h-5 text-emerald-500" />
                  Why Partner With FundTheHope?
                </h3>
                <ul className="space-y-3 text-sm text-muted-foreground">
                  <li className="flex items-start gap-3">
                    <span className="text-emerald-400 font-bold">0%</span>
                    <span>0% platform fee — Donations go to causes</span>
                  </li>
                  <li className="flex items-start gap-3">
                    <span className="text-emerald-400 font-bold">✓</span>
                    <span>Verified campaigns with full transparency</span>
                  </li>
                  <li className="flex items-start gap-3">
                    <span className="text-emerald-400 font-bold">✓</span>
                    <span>AI-powered tools for maximum impact</span>
                  </li>
                  <li className="flex items-start gap-3">
                    <span className="text-emerald-400 font-bold">✓</span>
                    <span>Multi-language support for global reach</span>
                  </li>
                </ul>
              </div>
            </div>

            {/* Right Side - Form */}
            <div>
              <div className="bg-card border border-border rounded-2xl p-8">
                <h2 className="text-2xl font-bold text-foreground mb-2">Get in Touch</h2>
                <p className="text-muted-foreground text-sm mb-6">
                  Fill out the form below and we'll get back to you within 48 hours.
                </p>

                {error && (
                  <div className="bg-red-500/10 border border-red-500/20 text-red-400 px-4 py-3 rounded-lg mb-6 text-sm">
                    {error}
                  </div>
                )}

                <form onSubmit={handleSubmit} className="space-y-5">
                  <div>
                    <Label className="text-sm text-foreground mb-2 block">Organization Name *</Label>
                    <Input
                      type="text"
                      placeholder="Your company or organization"
                      value={formData.company_name}
                      onChange={(e) => setFormData({...formData, company_name: e.target.value})}
                      required
                      className="bg-background"
                    />
                  </div>

                  <div>
                    <Label className="text-sm text-foreground mb-2 block">Contact Person *</Label>
                    <Input
                      type="text"
                      placeholder="Your full name"
                      value={formData.contact_name}
                      onChange={(e) => setFormData({...formData, contact_name: e.target.value})}
                      required
                      className="bg-background"
                    />
                  </div>

                  <div>
                    <Label className="text-sm text-foreground mb-2 block">Email Address *</Label>
                    <Input
                      type="email"
                      placeholder="you@company.com"
                      value={formData.email}
                      onChange={(e) => setFormData({...formData, email: e.target.value})}
                      required
                      className="bg-background"
                    />
                  </div>

                  <div>
                    <Label className="text-sm text-foreground mb-2 block">Partnership Type *</Label>
                    <div className="grid grid-cols-2 gap-3">
                      <button
                        type="button"
                        onClick={() => setFormData({...formData, partnership_type: 'corporate'})}
                        className={`p-4 rounded-xl border-2 text-left transition-all ${
                          formData.partnership_type === 'corporate'
                            ? 'border-emerald-500 bg-emerald-500/10'
                            : 'border-border hover:border-emerald-500/50'
                        }`}
                      >
                        <Building2 className={`w-5 h-5 mb-2 ${formData.partnership_type === 'corporate' ? 'text-emerald-500' : 'text-muted-foreground'}`} />
                        <span className={`text-sm font-medium ${formData.partnership_type === 'corporate' ? 'text-foreground' : 'text-muted-foreground'}`}>
                          Corporate
                        </span>
                      </button>
                      <button
                        type="button"
                        onClick={() => setFormData({...formData, partnership_type: 'nonprofit'})}
                        className={`p-4 rounded-xl border-2 text-left transition-all ${
                          formData.partnership_type === 'nonprofit'
                            ? 'border-teal-500 bg-teal-500/10'
                            : 'border-border hover:border-teal-500/50'
                        }`}
                      >
                        <Globe className={`w-5 h-5 mb-2 ${formData.partnership_type === 'nonprofit' ? 'text-teal-500' : 'text-muted-foreground'}`} />
                        <span className={`text-sm font-medium ${formData.partnership_type === 'nonprofit' ? 'text-foreground' : 'text-muted-foreground'}`}>
                          Nonprofit
                        </span>
                      </button>
                    </div>
                  </div>

                  <div>
                    <Label className="text-sm text-foreground mb-2 block">Message (Optional)</Label>
                    <textarea
                      placeholder="Tell us about your organization and partnership goals..."
                      value={formData.message}
                      onChange={(e) => setFormData({...formData, message: e.target.value})}
                      rows={4}
                      className="w-full px-4 py-3 bg-background border-2 border-emerald-500/40 rounded-xl text-foreground placeholder:text-muted-foreground focus:outline-none focus:ring-2 focus:ring-emerald-500/20 focus:border-emerald-500/60 resize-none"
                    />
                  </div>

                  <Button
                    type="submit"
                    disabled={loading}
                    className="w-full py-6 bg-gradient-to-r from-emerald-500 to-teal-600 hover:from-emerald-400 hover:to-teal-500 text-lg font-semibold"
                  >
                    {loading ? (
                      <Loader2 className="w-5 h-5 animate-spin" />
                    ) : (
                      <>
                        <Send className="w-5 h-5 mr-2" />
                        Submit Inquiry
                      </>
                    )}
                  </Button>
                </form>

                <p className="text-center text-xs text-muted-foreground mt-6">
                  Or email us directly at{' '}
                  <a href="mailto:partnership@fundthehope.com" className="text-emerald-400 hover:underline">
                    partnership@fundthehope.com
                  </a>
                </p>
              </div>
            </div>
          </div>
        </div>
      </main>
      <Footer />
    </div>
  );
};

export default PartnersPage;
